'use client'

import { useCallback } from 'react'
import { Review } from '@skop-app/lib-domain/types'
import { ElementStatus, ReviewStatus, ReviewType } from '@skop-app/lib-types'

import { useUserContext } from '@/context'
import {
  type OptionListRestrictionReason,
  resolveOptionListAccess,
} from '@/features/property/option.rules'
import { useUpdateField } from '@/services/mutations'
import { useUpsertReference } from '@/services/mutations/reference'
import { useUpsertReview } from '@/services/mutations/review'
import { resolveOptionModerationTransition } from '@/services/property/option.workflow'
import { getReviewMetadataValue } from '@/utils/reviews'

type ReviewDecisionAvailability = {
  canDecide: boolean
  reasonCode?: OptionListRestrictionReason | 'admin-required'
}

export const useReviewDecision = () => {
  const { mutateAsync: upsertReview } = useUpsertReview()
  const { mutateAsync: updateField } = useUpdateField()
  const { mutateAsync: upsertReference } = useUpsertReference()
  const { authorization, isAdmin, isGuest, organizationScope } =
    useUserContext()

  const getReviewDecisionAvailability = useCallback(
    (review?: Review): ReviewDecisionAvailability => {
      if (!review) {
        return {
          canDecide: false,
          reasonCode: 'admin-required',
        }
      }

      const isCreationReview =
        getReviewMetadataValue(review, 'isCreationReview') === 'true'

      if (review.type === ReviewType.REVIEW_OPTION && isCreationReview) {
        const access = resolveOptionListAccess({
          canUpdate: !isGuest && Boolean(authorization?.optionsAuth?.canUpdate),
          isListOwner:
            Boolean(review.organizationID) &&
            Boolean(organizationScope.activeOrganizationId) &&
            review.organizationID === organizationScope.activeOrganizationId,
          isListOwnerResolved: Boolean(review.organizationID),
        })

        return {
          canDecide: access.canManageList,
          reasonCode: access.restrictionReason ?? undefined,
        }
      }

      return {
        canDecide: isAdmin,
        reasonCode: isAdmin ? undefined : 'admin-required',
      }
    },
    [
      authorization?.optionsAuth?.canUpdate,
      isAdmin,
      isGuest,
      organizationScope.activeOrganizationId,
    ],
  )

  const resolveThread = async ({
    review,
    nextStatus,
  }: {
    review: Review
    nextStatus: ReviewStatus
  }) => {
    await upsertReview({
      upsertType: 'update',
      input: {
        id: review.id,
        status: nextStatus,
      },
    })
  }

  const applyCreationReviewDecision = async ({
    review,
    nextStatus,
  }: {
    review: Review
    nextStatus: ReviewStatus
  }) => {
    const availability = getReviewDecisionAvailability(review)
    if (!availability.canDecide) {
      throw new Error('Review decision is not allowed in the current scope')
    }

    const elementOrganizationID = getReviewMetadataValue(
      review,
      'organizationID',
    )
    const isCreationReview =
      getReviewMetadataValue(review, 'isCreationReview') === 'true'

    if (!isCreationReview) {
      await resolveThread({ review, nextStatus })
      return
    }

    if (review.type === ReviewType.REVIEW_OPTION) {
      if (nextStatus === ReviewStatus.REJECTED) {
        throw new Error(
          'Option validation refusals must use the option refusal flow',
        )
      }

      const { optionStatus, reviewStatus } = resolveOptionModerationTransition({
        nextStatus,
        optionOrganizationID: elementOrganizationID,
        listOwnerOrganizationID: review.organizationID,
      })

      await updateField({
        id: review.elementID,
        status: optionStatus,
      })

      if (reviewStatus) {
        await upsertReview({
          upsertType: 'update',
          input: {
            id: review.id,
            status: reviewStatus,
          },
        })
      }

      return
    }

    const skopOrganizationId = process.env.NEXT_PUBLIC_SKOP_ORGID
    if (!skopOrganizationId) {
      throw new Error('NEXT_PUBLIC_SKOP_ORGID is not defined')
    }

    const shouldRestrictElement =
      Boolean(elementOrganizationID) &&
      elementOrganizationID !== skopOrganizationId &&
      review.organizationID === skopOrganizationId &&
      nextStatus === ReviewStatus.REJECTED
    const nextElementStatus =
      nextStatus === ReviewStatus.ACCEPTED
        ? ElementStatus.ACCEPTED
        : nextStatus === ReviewStatus.REJECTED
          ? ElementStatus.REJECTED
          : ElementStatus.PENDING

    const elementPayload = shouldRestrictElement
      ? { organizationID: elementOrganizationID }
      : { status: nextElementStatus }

    if (review.type === ReviewType.REVIEW_REFERENCE) {
      await upsertReference({
        isNew: false,
        input: {
          id: review.elementID,
          byUserID: review.byUserID,
          ...elementPayload,
        },
      })
    } else {
      await updateField({
        id: review.elementID,
        ...elementPayload,
      })
    }

    await upsertReview({
      upsertType: 'update',
      input: {
        id: review.id,
        status: nextStatus,
        ...(shouldRestrictElement && elementOrganizationID
          ? { organizationID: elementOrganizationID }
          : {}),
      },
    })
  }

  return {
    getReviewDecisionAvailability,
    resolveThread,
    applyCreationReviewDecision,
  }
}
